import { useState, useRef } from 'react';
import { ArrowLeft, Play, Pause, CheckCircle } from 'lucide-react';
import { Surah } from '../data/quran';

interface SurahReaderProps {
  surah: Surah;
  onBack: () => void;
  onComplete: () => void;
}

export function SurahReader({ surah, onBack, onComplete }: SurahReaderProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [currentVerseId, setCurrentVerseId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const verseRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioEnded = () => {
      setIsPlaying(false);
      setIsCompleted(true);
      setCurrentVerseId(null);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const time = audioRef.current.currentTime;
    
    // Find active verse
    const activeVerse = surah.verses.find(v => 
        v.startTime !== undefined && v.endTime !== undefined && 
        time >= v.startTime && time < v.endTime
    );

    if (activeVerse && activeVerse.id !== currentVerseId) {
        setCurrentVerseId(activeVerse.id);
        // Scroll to verse
        verseRefs.current[activeVerse.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="min-h-screen bg-amber-50/30 text-slate-900 flex flex-col pb-32">
      {/* Header */}
      <div className="bg-white px-6 py-4 pt-safe-top border-b border-amber-100 sticky top-0 z-10 shadow-sm flex items-center justify-between">
        <button 
          onClick={onBack}
          className="p-2 -ml-2 rounded-full hover:bg-amber-50 text-slate-600 transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="text-center">
          <span className="text-xs uppercase tracking-widest text-emerald-600 font-semibold mb-1 block">Surah {surah.id}</span>
          <h2 className="text-2xl font-bold font-serif">{surah.englishName}</h2>
          <p className="text-sm text-slate-500">{surah.meaning}</p>
        </div>
        <div className="w-10"></div> {/* Spacer */}
      </div>

      {/* Content */}
      <div className="flex-1 p-6 pb-32 max-w-md mx-auto w-full">
        {/* Audio Player Hidden but functional */}
        <audio 
          ref={audioRef} 
          src={surah.audioUrl} 
          onEnded={handleAudioEnded}
          onTimeUpdate={handleTimeUpdate}
        />

        <div className="space-y-8">
          {surah.verses.map((verse) => (
            <div 
                key={verse.id} 
                ref={el => verseRefs.current[verse.id] = el}
                className={`text-center space-y-4 border-b border-stone-50 pb-8 last:border-0 transition-all duration-500 ${currentVerseId === verse.id ? 'scale-105 bg-emerald-50/50 py-4 rounded-xl' : 'opacity-80'}`}
            >
              {/* Arabic */}
              <p 
                className={`text-3xl font-serif leading-loose ${currentVerseId === verse.id ? 'text-emerald-800 font-bold' : 'text-slate-800'}`} 
                dir="rtl" 
                style={{ fontFamily: 'Traditional Arabic, serif' }}
              >
                {verse.arabic}
              </p>
              
              {/* Transliteration */}
              <p className={`font-medium text-sm ${currentVerseId === verse.id ? 'text-emerald-700' : 'text-emerald-700/70'}`}>
                {verse.transliteration}
              </p>
              
              {/* Translation */}
              <p className="text-stone-500 text-sm italic">
                {verse.translation}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Floating Action Bar */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-stone-100 p-6 safe-area-bottom shadow-lg z-20">
        <div className="max-w-md mx-auto">
            {/* Reciter Credit */}
            <div className="text-center space-y-1 mb-4">
                <p className="text-xs font-medium text-emerald-700">
                    Recitation by Mishary Rashid Alafasy
                </p>
                <p className="text-[10px] text-slate-400 opacity-60">
                    Sourced from Mp3Quran.net • v2.1
                </p>
            </div>

            <div className="flex gap-4">
                <button 
                    onClick={togglePlay}
                    className="flex-1 bg-stone-100 hover:bg-stone-200 text-slate-800 font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    {isPlaying ? "Pause Recitation" : "Listen"}
                </button>
                
                <button 
                    onClick={onComplete}
                    disabled={!isCompleted}
                    className={`flex-1 font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
                        isCompleted 
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg cursor-pointer' 
                        : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                    }`}
                >
                    <CheckCircle className="w-5 h-5" />
                    {isCompleted ? "Complete Day 1" : "Listen to Complete"}
                </button>
            </div>
        </div>
      </div>
    </div>
  );
}
